/**
 * Linked product swap
 *
 * Swaps the main product area (`.product--half`) in place — without a full
 * page reload — when a linked-product swatch is clicked. Mirrors the behaviour
 * of braveface.com's Vue-based implementation, but in dependency-free vanilla
 * JS: the theme's interactive UI is built from auto-upgrading custom elements
 * (`variant-selects`, `product-form`, `quantity-input`, ...) and native
 * `<details>` accordions, so the swapped-in markup wires itself up. The only
 * thing needing manual re-initialisation is the Flickity gallery carousel.
 *
 * Fetches the target product's page and replaces the live `.product--half`
 * node with the freshly rendered one. A full-page fetch (rather than the
 * Section Rendering API) is used deliberately: cadence's `.product--half` is
 * assembled from JSON-template `section.blocks`, whose section id is
 * product-specific (`template--{productId}__main`), so it can't be requested
 * reliably against a *different* product's URL. The full product page always
 * contains the correctly rendered markup for that product.
 */
(function () {
  'use strict';

  var SWAP_TARGET = '.product--half';
  var LINK_SELECTOR = '.linked-product__tile-link';
  var CAROUSEL_SELECTOR = '.product__carousel';
  var BOUND_FLAG = '__linkedProductSwapBound';

  var swapAbort = null;
  var swapInFlight = false;
  var popstateBound = false;

  function getProductEl() {
    return document.querySelector(SWAP_TARGET);
  }

  function getProductPath(url) {
    try {
      var parsed = new URL(url, window.location.origin);
      if (!/\/products\/[^/?#]+/.test(parsed.pathname)) {
        return null;
      }
      return parsed.pathname + parsed.search;
    } catch (e) {
      return null;
    }
  }

  function urlsMatch(a, b) {
    try {
      return (
        new URL(a, window.location.origin).pathname ===
        new URL(b, window.location.origin).pathname
      );
    } catch (e) {
      return a === b;
    }
  }

  function destroyCarousel(root) {
    if (!window.Flickity || !root) {
      return;
    }
    var el = root.querySelector(CAROUSEL_SELECTOR);
    if (!el) {
      return;
    }
    var instance = window.Flickity.data(el);
    if (instance && typeof instance.destroy === 'function') {
      try {
        instance.destroy();
      } catch (e) {
        // ignore destroy errors from partially initialised instances
      }
    }
  }

  function initCarousel(root) {
    if (!window.Flickity || !root) {
      return;
    }
    var el = root.querySelector(CAROUSEL_SELECTOR);
    if (!el || window.Flickity.data(el)) {
      return;
    }
    var flkty = new window.Flickity(el, { percentPosition: false });
    flkty.once('ready', function () {
      window.dispatchEvent(new Event('resize'));
    });
  }

  function updateDocumentTitle(doc, newProduct) {
    // Prefer the fetched page's real <title>; fall back to the product heading.
    var docTitle = doc.querySelector('title');
    if (docTitle && docTitle.textContent.trim()) {
      document.title = docTitle.textContent.trim();
      return;
    }

    var titleEl = newProduct.querySelector('.product__title');
    if (titleEl && titleEl.textContent.trim()) {
      document.title = titleEl.textContent.trim();
    }
  }

  function swapProduct(url, options) {
    options = options || {};
    var pushState = options.pushState !== false;
    var path = getProductPath(url);

    if (!path) {
      window.location.href = url;
      return;
    }

    if (pushState && urlsMatch(path, window.location.href)) {
      return;
    }

    var liveProduct = getProductEl();
    if (!liveProduct) {
      window.location.href = url;
      return;
    }

    if (swapAbort) {
      swapAbort.abort();
    }
    swapAbort = new AbortController();
    swapInFlight = true;
    liveProduct.classList.add('is-loading');

    fetch(path, {
      signal: swapAbort.signal,
      headers: { Accept: 'text/html' }
    })
      .then(function (response) {
        if (!response.ok) {
          throw new Error('Product page fetch failed: ' + response.status);
        }
        return response.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var nextProduct = doc.querySelector(SWAP_TARGET);
        var currentProduct = getProductEl();

        if (!nextProduct || !currentProduct) {
          throw new Error('Missing .product--half markup in fetched product page');
        }

        destroyCarousel(currentProduct);
        var imported = document.importNode(nextProduct, true);
        currentProduct.replaceWith(imported);

        updateDocumentTitle(doc, imported);

        if (pushState) {
          history.pushState({ linkedProduct: true }, '', path);
        }

        initCarousel(imported);
      })
      .catch(function (error) {
        if (error && error.name === 'AbortError') {
          return;
        }
        console.error(error);
        window.location.href = url;
      })
      .finally(function () {
        swapInFlight = false;
        var current = getProductEl();
        if (current) {
          current.classList.remove('is-loading');
        }
      });
  }

  function onClick(event) {
    var target = event.target;
    var link = target && target.closest ? target.closest(LINK_SELECTOR) : null;
    if (!link) {
      return;
    }

    var href = link.getAttribute('href');
    if (!href || !getProductPath(href)) {
      return;
    }

    // Let modified clicks (new tab, download, etc.) behave normally.
    if (
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey ||
      event.button !== 0
    ) {
      return;
    }

    // Clicking the already-active tile is a no-op.
    var tile = link.closest('.linked-product__tile');
    if (tile && tile.classList.contains('is-active')) {
      event.preventDefault();
      return;
    }

    if (swapInFlight) {
      event.preventDefault();
      return;
    }

    event.preventDefault();
    swapProduct(href, { pushState: true });
  }

  function bindPopState() {
    if (popstateBound) {
      return;
    }
    popstateBound = true;

    window.addEventListener('popstate', function () {
      if (!getProductEl()) {
        return;
      }
      swapProduct(window.location.href, { pushState: false });
    });
  }

  function init() {
    if (document[BOUND_FLAG] || !getProductEl()) {
      return;
    }
    document[BOUND_FLAG] = true;

    // Delegated on document so it keeps working after the node is swapped.
    document.addEventListener('click', onClick);
    bindPopState();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
